import { useEffect, useState } from 'react'
import { collection, query, where, orderBy, onSnapshot, type QueryConstraint } from 'firebase/firestore'
import { db } from '@fleetops/firebase'
import { firestorePaths } from '@fleetops/types'
import type { Trailer } from '@fleetops/types'
import { useSession } from '@/store/auth.store'
import { fleetQueryChunks, fleetScopeKey } from '@/lib/fleetScope'
import { subscriptionError } from '@/lib/firestoreError'

export function useTrailers() {
  const session = useSession()
  const [trailers, setTrailers] = useState<Trailer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const scopeKey = fleetScopeKey(session)

  useEffect(() => {
    if (!session) return

    // One listener per `in`-sized chunk of the caller's fleets (usually just
    // one). Ordering has to be redone on the merged set: each chunk is sorted
    // server-side, the union of them is not.
    const chunks = fleetQueryChunks(session)
    const perChunk = new Map<number, Trailer[]>()

    // Stored for the callers that render it, reported for the ones that only
    // read `trailers` and would otherwise show an empty fleet in silence.
    const report = subscriptionError('os reboques', () => setIsLoading(false))

    const unsubscribes = chunks.map((chunk, i) => {
      const constraints: QueryConstraint[] = [
        where('tenantId', '==', session.tenantId),
        ...(chunk ? [where('fleetId', 'in', chunk)] : []),
        orderBy('createdAt', 'desc'),
      ]
      return onSnapshot(
        query(collection(db, firestorePaths.trailers(session.tenantId)), ...constraints),
        (snap) => {
          perChunk.set(i, snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Trailer))
          const byId = new Map<string, Trailer>()
          for (const list of perChunk.values()) for (const t of list) byId.set(t.id, t)
          setTrailers([...byId.values()].sort(
            (a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0),
          ))
          setIsLoading(false)
        },
        (err) => {
          setError(err)
          report(err)
        },
      )
    })

    return () => unsubscribes.forEach((u) => u())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.tenantId, session?.role, scopeKey])

  return { trailers, isLoading, error }
}
